import React, { useMemo } from 'react';

import PropTypes from 'prop-types';
import { FormattedNumber } from 'react-intl';

import { MDash } from 'ui-components/components/mdash';
import { Text, TextType } from 'ui-components/components/text';
import { emptyArray } from 'ui-components/default-props/empty';
import { StockLevelsPropType } from '../../../../../../prop-types/stock-levels';

import { InventoryService } from '../../../../../../services/inventory-service';
import { StockLevelWrapper } from './stock-level-wrapper';
import { AvailableStockLevel } from './available-stock';
import { TotalStockLevel } from './total-stock';
import { BlankBoxStockLevel } from './blank-box-stock';
import { shipmentItemInventoryStatus } from '../../../../fulfillment-plan/sections/shipment-items/helpers/get-item-inventory-status';
import { getMappedStockLevels } from './helpers';

import style from './item-stock.module.less';

export const InventoryProviderStockLevels = ({
  itemInventoryStatus,
  shouldShowInventoryInfoUiTreatments = false,
  stockLevels = emptyArray,
}) => {
  const { didSucceed, availableStock, totalStock, blankBoxStock } = useMemo(() => getMappedStockLevels(stockLevels), [stockLevels]);

  if (!stockLevels.length) {
    return null;
  }

  if (didSucceed) {
    return (
      <StockLevelWrapper>
        {availableStock && (
          <AvailableStockLevel
            quantity={availableStock?.quantity}
            shouldShowInventoryInfoUiTreatments={shouldShowInventoryInfoUiTreatments}
            status={itemInventoryStatus}
          />
        )}
        {totalStock && <TotalStockLevel quantity={totalStock?.quantity} />}
        {blankBoxStock && <BlankBoxStockLevel quantity={blankBoxStock?.quantity} />}
      </StockLevelWrapper>
    );
  }

  // This is a fallback approach that will render purely data-driven stock levels
  // and is expected to only be used in the event that a merchant's inventory provider
  // sends us unmapped stock levels.
  return (
    <StockLevelWrapper>
      {stockLevels.map((stockLevel, index) => {
        if (stockLevel.label === InventoryService.StockLevelQuantityType.Allocated) {
          return null;
        }
        return (
          <React.Fragment key={`${stockLevel.type}-${index}`}>
            <div className={style['stock-level-container']}>
              <Text className={style['stock-level-label']} element='label' textType={TextType.Caption} value={stockLevel.label} />
              <Text
                className={style['stock-level']}
                element='span'
                textType={TextType.Caption}
                value={stockLevel.quantity === undefined ? <MDash /> : <FormattedNumber value={stockLevel.quantity} />}
              />
            </div>
          </React.Fragment>
        );
      })}
    </StockLevelWrapper>
  );
};

InventoryProviderStockLevels.propTypes = {
  itemInventoryStatus: PropTypes.oneOf(Object.values(shipmentItemInventoryStatus)).isRequired,
  shouldShowInventoryInfoUiTreatments: PropTypes.bool,
  stockLevels: StockLevelsPropType,
};
